import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ShieldCheck, Search } from "lucide-react";
import { NAV, SITE_NAME } from "../../config/site";
import SearchOverlay from "../SearchOverlay";

function DesktopDropdown({ section }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={`nav-${section.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
      >
        {section.label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 w-72 rounded-lg border border-slate-200 bg-white py-2 shadow-lg">
          {section.items.map((item) => (
            <Link
              key={item.to + item.label}
              to={item.to}
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-[#F3F5F8] hover:text-[#0B1F3A]"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenSection(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-40 bg-[#0B1F3A] shadow-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2.5"
          aria-label={`${SITE_NAME} – Accueil`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2BB08A]">
            <ShieldCheck className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight text-white">
            {SITE_NAME}
            <span className="text-[#2BB08A]">.fr</span>
          </span>
        </Link>

        <nav aria-label="Navigation principale" className="hidden items-center lg:flex">
          {NAV.map((section) =>
            section.items ? (
              <DesktopDropdown key={section.label} section={section} />
            ) : (
              <Link
                key={section.label}
                to={section.to}
                data-testid="nav-jeu-responsable"
                className="rounded-md px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
              >
                {section.label}
              </Link>
            ),
          )}
        </nav>

        <button
          type="button"
          data-testid="search-open"
          onClick={() => setSearchOpen(true)}
          aria-label="Rechercher dans le site"
          className="rounded-md p-2 text-white transition-colors hover:bg-white/10"
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </button>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          onClick={() => setMobileOpen((v) => !v)}
          className="rounded-md p-2 text-white transition-colors hover:bg-white/10 lg:hidden"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 bottom-0 top-[60px] z-40 overflow-y-auto bg-[#0B1F3A] px-4 pb-24 pt-4 lg:hidden">
          {NAV.map((section) =>
            section.items ? (
              <div key={section.label} className="border-b border-white/10">
                <button
                  type="button"
                  aria-expanded={openSection === section.label}
                  onClick={() =>
                    setOpenSection(openSection === section.label ? null : section.label)
                  }
                  className="flex w-full items-center justify-between py-3.5 text-left font-display text-base font-semibold text-white"
                >
                  {section.label}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      openSection === section.label ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {openSection === section.label && (
                  <div className="pb-3">
                    {section.items.map((item) => (
                      <Link
                        key={item.to + item.label}
                        to={item.to}
                        className="block rounded-md px-3 py-2.5 text-[15px] text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={section.label}
                to={section.to}
                className="block border-b border-white/10 py-3.5 font-display text-base font-semibold text-white"
              >
                {section.label}
              </Link>
            ),
          )}
        </div>
      )}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
